import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useSocket, useSocketEvent } from '@astro/socket'

/**
 * App-wide listener for the astrologer's video-call invitation. When the
 * astrologer starts a consultation (booking PATCHed to ready_to_start), the
 * BFF relays a `consultation:invite` socket event to the seeker — this modal
 * pops wherever they are in the app. Accepting moves the booking to
 * in_progress (on room join) and drops them into the consultation room.
 */
export default function ConsultationInviteModal() {
  const [invite, setInvite] = useState(null)
  const [accepting, setAccepting] = useState(false)
  const { emit } = useSocket()
  const navigate = useNavigate()
  const location = useLocation()

  useSocketEvent('consultation:invite', (payload) => {
    if (!payload?.bookingId) return
    // Already in this consultation's room — nothing to ask.
    if (location.pathname === `/consultations/${payload.bookingId}/video`) return
    setInvite(payload)
  })

  // Astrologer withdrew (ended before the seeker answered).
  useSocketEvent('consultation:end', (payload) => {
    if (invite && String(payload?.bookingId) === String(invite.bookingId)) setInvite(null)
  })

  if (!invite) return null

  const accept = () => {
    setAccepting(true)
    // Joining the room is what flips the booking to in_progress (see
    // VideoConsultationPage) — accepting just signals and navigates. The
    // room itself is resolved there from the booking id (astro-{id}).
    emit('consultation:accept', { toUserId: invite.fromUserId, bookingId: invite.bookingId })
    const target = `/consultations/${invite.bookingId}/video`
    setInvite(null)
    setAccepting(false)
    // `invited` lets the room admit the seeker even when the backend rejected
    // the ready_to_start status PATCH (its validation whitelist gap) — the
    // live invitation itself is the proof the astrologer has started.
    navigate(target, { state: { invited: true } })
  }

  const decline = () => {
    emit('consultation:decline', { toUserId: invite.fromUserId, bookingId: invite.bookingId })
    setInvite(null)
  }

  return (
    <div className="fixed inset-0 z-100 flex items-center justify-center px-6 py-8 overflow-y-auto bg-black/70 backdrop-blur-sm">
      <div className="w-full max-w-md my-auto rounded-2xl overflow-hidden bg-[#1F1913] border border-[#C8922A]/30 shadow-2xl text-center">
        <div className="h-1 w-full bg-gradient-to-r from-[#C8922A] via-[#E6C878] to-[#C8922A]" />
        <div className="p-8">
          <div className="mx-auto w-16 h-16 rounded-full bg-gradient-to-tr from-[#C8922A] to-[#E6C878] flex items-center justify-center text-3xl animate-pulse">
            📹
          </div>
          <p className="font-cinzel text-xl text-white mt-5">Your consultation is ready</p>
          <p className="text-sm text-white/50 mt-2">
            {invite.fromName || 'Your astrologer'} is inviting you to join the video consultation
            {invite.bookingId ? ` for booking BKNG${invite.bookingId}` : ''}.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-7">
            <button
              type="button"
              disabled={accepting}
              onClick={accept}
              className="hero-btn-primary w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full px-8 py-3.5 text-xs font-bold uppercase tracking-[0.2em] disabled:opacity-60"
            >
              {accepting ? 'Connecting…' : 'Join Now'}
            </button>
            <button
              type="button"
              onClick={decline}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full px-8 py-3.5 text-xs font-bold uppercase tracking-[0.2em] border border-white/20 text-white/60 hover:text-white hover:border-white/40 transition-colors"
            >
              Not Now
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
